import React, { useState, useEffect, useContext } from "react";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import PersonIcon from "@material-ui/icons/Person";
import AndroidIcon from "@material-ui/icons/Android";
import GlobalContext from "../../GlobalContext";
import constants from "../../tools/constants";
import UserType from "../../types/User";
import theme from "../../theme";

const formatProfileName = (firstName?: string, lastName?: string) => {
  const name = [firstName, lastName].filter(Boolean).join(" ");
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const chipStyle = {
  height: 22,
  fontSize: "0.7rem",
  marginTop: 0,
  maxWidth: "100%",
};

const labelPrefixStyle: React.CSSProperties = {
  fontSize: "0.65rem",
  textTransform: "lowercase",
  fontWeight: 400,
};

const labelNameStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 500,
};

const textVariantColors = {
  prefix: "rgba(0, 0, 0, 0.45)",
  name: "rgba(0, 0, 0, 0.72)",
  icon: "rgba(0, 0, 0, 0.45)",
};

const textVariantStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "flex-end",
  gap: 4,
  marginTop: 2,
};

export type LastLabeledByBadgeVariant = "chip" | "text";

interface LastLabeledByBadgeProps {
  lastLabeledBy: string;
  variant?: LastLabeledByBadgeVariant;
}

const LabeledByLabel = ({
  label,
  prefixStyle = labelPrefixStyle,
  nameStyle = labelNameStyle,
}: {
  label: string;
  prefixStyle?: React.CSSProperties;
  nameStyle?: React.CSSProperties;
}) => (
  <span>
    <span style={prefixStyle}>Labellisée par : </span>
    <span style={nameStyle}>{label}</span>
  </span>
);

const LastLabeledByBadge = ({
  lastLabeledBy,
  variant = "chip",
}: LastLabeledByBadgeProps) => {
  const { appFetch } = useContext(GlobalContext) as { appFetch: Function };
  const [label, setLabel] = useState<string>(lastLabeledBy);
  const [tooltip, setTooltip] = useState<string>(lastLabeledBy);
  const isModel = lastLabeledBy === "model";

  useEffect(() => {
    if (isModel) {
      setLabel("Modèle");
      setTooltip("Labellisée par le modèle");
      return;
    }

    let cancelled = false;

    const fetchUser = async () => {
      try {
        const user = (await appFetch(
          `${constants.paths.USERS_PATH}/${lastLabeledBy}`
        )) as UserType;
        if (cancelled) return;

        const name = formatProfileName(user.firstName, user.lastName);
        setLabel(name || user.email || lastLabeledBy);
        setTooltip(user.email || lastLabeledBy);
      } catch {
        if (!cancelled) {
          setLabel(lastLabeledBy);
          setTooltip(lastLabeledBy);
        }
      }
    };

    setLabel(lastLabeledBy);
    setTooltip(lastLabeledBy);
    fetchUser();

    return () => {
      cancelled = true;
    };
  }, [appFetch, isModel, lastLabeledBy]);

  const IconComponent = isModel ? AndroidIcon : PersonIcon;
  const secondaryColor = theme.palette.secondary.main;
  const isTextVariant = variant === "text";
  const iconColor = isTextVariant ? textVariantColors.icon : secondaryColor;
  const iconStyle: React.CSSProperties = {
    fontSize: 14,
    color: iconColor,
    ...(variant === "chip"
      ? { marginLeft: 4, marginRight: -6 }
      : { flexShrink: 0 }),
  };

  const content = (
    <LabeledByLabel
      label={label}
      prefixStyle={
        isTextVariant
          ? { ...labelPrefixStyle, color: textVariantColors.prefix }
          : undefined
      }
      nameStyle={
        isTextVariant
          ? { ...labelNameStyle, color: textVariantColors.name }
          : undefined
      }
    />
  );

  if (variant === "text") {
    return (
      <Tooltip title={`Labellisée par ${tooltip}`}>
        <span style={textVariantStyle}>
          <IconComponent style={iconStyle} />
          {content}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Labellisée par ${tooltip}`}>
      <Chip
        color="secondary"
        icon={<IconComponent style={iconStyle} />}
        label={content}
        variant="outlined"
        style={{
          ...chipStyle,
          borderRadius: theme.shape.borderRadius,
        }}
      />
    </Tooltip>
  );
};

export default LastLabeledByBadge;
